import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Button, LoadingSpinner, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { PRODUCT_GROUP_IMPORT_SAMPLE_CSV } from "../../constants/productImportSample";
import { useProductFileUpload } from "../../hooks/useProductFileUpload";
import {
  productGroupFileUploadJsonSchema,
  productGroupFileUploadUISchema,
} from "./productFormSchema";
import "./productManagement.scss";

const ProductGroupFileUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalToast, setModalToast] = useState([]);
  const { handleImportFileCall, handleDownloadSampleFile } = useProductFileUpload({
    sampleCsv: PRODUCT_GROUP_IMPORT_SAMPLE_CSV,
  });

  const setToast = useCallback((text, type, id) => {
    setModalToast([{ text, type, id }]);
  }, []);

  const onCloseModal = useCallback(() => {
    setFormData({});
    setIsSubmitting(false);
    setModalToast([]);
    onClose();
  }, [onClose]);

  const validateFormData = useCallback((data) => {
    const files = data?.productGroupFileData || [];
    if (files.length === 0) {
      return ["File is required"];
    }
    return [];
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const errors = validateFormData(formData);
    if (errors.length > 0) {
      setIsSubmitting(false);
      setModalToast(
        errors.map((error, index) => ({
          text: error,
          type: "error",
          id: `validation-error-${index}`,
        }))
      );
      return;
    }

    const files = formData?.productGroupFileData || [];

    try {
      const uploadResponse = await handleImportFileCall(files);
      if (!uploadResponse?.status) {
        setToast(uploadResponse?.message || "File upload failed", "error", 5);
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      setFormData({});

      if (onSuccess) {
        onSuccess();
      }
      onCloseModal();
    } catch (error) {
      setToast(
        error?.message || "An unexpected error occurred during file upload",
        "error",
        5
      );
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    formData,
    validateFormData,
    handleImportFileCall,
    setToast,
    onSuccess,
    onCloseModal,
  ]);

  if (!isOpen) return null;

  return (
    <Dialog
      className="product-group-file-upload-modal width-md"
      open={isOpen}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" direction="row">
          <Grid item xs={10}>
            <h2
              data-testid="product-group-file-upload-title"
              style={{ marginLeft: "18%", textAlign: "center" }}
            >
              Upload Product Groups
            </h2>
          </Grid>
          <Grid item xs={1} display="flex" justifyContent="flex-end">
            <CloseIcon
              onClick={onCloseModal}
              className="cursor-pointer"
              data-testid="product-group-file-upload-close"
            />
          </Grid>
        </Grid>
        <ToastContainer
          data={modalToast}
          removeToast={() => setModalToast([])}
          showActionIcon
        />
      </DialogTitle>
      <DialogContent sx={{ overflowX: "hidden" }}>
        <div className="my-5 product-group-upload-wrapper">
          <JsonFormsWrapper
            jsonschema={productGroupFileUploadJsonSchema}
            uischema={productGroupFileUploadUISchema}
            data={formData}
            onChange={(val) => {
              setIsSubmitting(false);
              setFormData(val);
            }}
          />
        </div>
        <Button
          size="small"
          variant="secondary"
          onClick={() =>
            handleDownloadSampleFile("text/csv", "product_group_sample.csv")
          }
          className="download-sample-file"
        >
          Download sample file <DownloadIcon fontSize="small" />
        </Button>
      </DialogContent>
      <DialogActions className="mt-5 display-flex justify-content-end align-items-center">
        {isSubmitting && (
          <LoadingSpinner
            data-testid="upload-loading-spinner"
            size={{ height: "24px", width: "24px" }}
          />
        )}
        <Button variant="primary" disabled={isSubmitting} onClick={handleSubmit}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProductGroupFileUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ProductGroupFileUploadModal;
